import { Search } from "lucide-react";
import { useState, useEffect } from "react";

export default function HeroSection({ searchValue, onSearchChange }) {
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  
  // Items from bottom navbar for search placeholder animation
  const searchItems = ["Scoops", "Yaari", "Events", "Discover", "Roomates", "Career Guidance"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % searchItems.length);
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="md:min-h-screen bg-gradient-to-b from-orange-50 to-[#7B189F]/10 flex flex-col">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;700&family=Afacad:wght@400;500;600&display=swap');
        .fraunces-font {
          font-family: 'Fraunces', serif;
        }
        .afacad-font {
          font-family: 'Afacad', sans-serif;
        }
      `}</style>
      {/* Top Content Section */}
      <div className="flex-1 pt-6 sm:pt-16 md:pt-20 lg:pt-24 xl:pt-28 2xl:pt-32 pb-4 sm:pb-8 md:pb-10 lg:pb-12">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 3xl:px-24">
          <div className="relative max-w-5xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-7xl mx-auto text-center">
            {/* Floating Icons - Hidden on mobile, visible on larger screens */}
            <div className="hidden lg:block absolute -left-16 xl:-left-20 2xl:-left-24 3xl:-left-32 top-0 w-12 h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 bg-white rounded-full shadow-md">
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src="/assets/pencil-icon.png"
                  alt="Mic"
                  className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10"
                />
              </div>
            </div>
            <div className="hidden lg:block absolute -right-16 xl:-right-20 2xl:-right-24 3xl:-right-32 top-0 w-12 h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 bg-white rounded-full shadow-md">
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src="/assets/app-icon.png"
                  alt="Event"
                  className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10"
                />
              </div>
            </div>

            {/* Heading */}
            <h1 className="fraunces-font font-bold text-[#7B189F] px-4 sm:px-0" style={{ 
              fontSize: 'clamp(28px, 2.7vw, 52px)', 
              fontWeight: 700,
              lineHeight: '1.2',
              marginBottom: 'clamp(12px, 0.83vw, 16px)'
            }}>
              Your Desi Community Hub –
            </h1>
            <h1 className="fraunces-font font-bold text-[#7B189F] px-4 sm:px-0" style={{ 
              fontSize: 'clamp(28px, 2.7vw, 52px)', 
              fontWeight: 700,
              lineHeight: '1.2',
              marginBottom: 'clamp(16px, 1.11vw, 20px)'
            }}>
              Connecting, Sharing, Thriving Together
            </h1>

            {/* Subtext */}
            <p className="fraunces-font max-w-xl sm:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 sm:px-6 md:px-8 text-gray-700" style={{ 
              fontSize: 'clamp(12px, 0.83vw, 16px)', 
              fontWeight: 400,
              lineHeight: '1.5',
              marginBottom: 'clamp(24px, 2.08vw, 40px)'
            }}>
              Find a roommate, ride sharing, suggestions on movies &<br className="hidden sm:inline" />
              travel, get advised on your visa, and do a lot more.
            </p>

            {/* Search Section */}
            <div className="relative max-w-xl sm:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto mb-4 md:mb-0">
              {/* Floating icons beside search bar - Hidden on mobile */}
              <div className="hidden lg:block absolute -left-12 xl:-left-16 2xl:-left-20 3xl:-left-24 top-1/2 -translate-y-1/2 w-12 h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 bg-white rounded-full shadow-md">
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src="/assets/shopping-cart-icon.png"
                    alt="Cart"
                    className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10"
                  />
                </div>
              </div>
              <div className="hidden lg:block absolute -right-12 xl:-right-16 2xl:-right-20 3xl:-right-24 top-1/2 -translate-y-1/2 w-12 h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 bg-white rounded-full shadow-md">
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src="/assets/house-icon.png"
                    alt="House"
                    className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10"
                  />
                </div>
              </div>

              {/* Search bar */}
              <div className="relative mx-4 sm:mx-6 md:mx-8 lg:mx-0">
                <Search className="absolute left-4 sm:left-5 md:left-6 top-1/2 -translate-y-1/2 text-gray-500 z-10" style={{ width: 'clamp(16px, 0.83vw, 20px)', height: 'clamp(16px, 0.83vw, 20px)' }} />
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder=""
                    className="w-full border border-black rounded-full shadow focus:outline-none focus:ring-2 focus:ring-[#7B189F] bg-white"
                    style={{
                      paddingLeft: 'clamp(40px, 2.08vw, 50px)',
                      paddingRight: 'clamp(16px, 0.83vw, 20px)',
                      paddingTop: 'clamp(10px, 0.83vw, 14px)',
                      paddingBottom: 'clamp(10px, 0.83vw, 14px)',
                      fontSize: 'clamp(14px, 0.83vw, 16px)'
                    }}
                  />
                  {!searchValue && (
                    <div className="absolute top-1/2 -translate-y-1/2 pointer-events-none" style={{ left: 'clamp(40px, 2.08vw, 50px)' }}>
                      <span className="text-gray-400" style={{ fontSize: 'clamp(14px, 0.83vw, 16px)' }}>
                        Search for{" "}
                        <span className="text-[#7B189F] font-medium transition-opacity duration-500" key={currentPlaceholderIndex}>
                          {searchItems[currentPlaceholderIndex]}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Landmarks Image - As a separate stacked element */}
      <div className="relative w-full md:mt-auto">
        <img
          src="/assets/landmarks-background.png"
          alt="World Landmarks"
          className="w-full h-auto object-contain max-h-32 sm:max-h-40 md:max-h-48 lg:max-h-64 xl:max-h-72 2xl:max-h-80 3xl:max-h-96"
        />
      </div>
    </div>
  );
}
